import uuid from 'uuid-random';
import {
  convertFromRaw,
  convertToRaw,
  DraftInlineStyleType,
  EditorState,
  RawDraftContentBlock,
  RawDraftContentState,
} from 'draft-js';
import { ColorPickerTool } from '../../components/EditTool/TextEditor/ColorPickerTool';
import { convertSectionToString } from '../index';
import { parseWordsAndSymbols } from './common';
import {
  ContentLine,
  EmergentContentSection,
  EmergentExemplar,
  ExemplarWord,
  Image,
  Section,
  WordsAndSentencesContent,
} from '../../types';
import { EmergentTextEditorSection, EmergentTextEditorSideState, TextEditorToolbar } from '../../types/textEditor';
import { COLORS, TEXT_TOOL_COLORS } from '../../constants';
import { BLOCK_TYPES, DEFAULT_STYLES, LINE_BREAKS } from '../../constants/textEditor';
import BoldIcon from '../../assets/bold-tool.png';
import ItalicIcon from '../../assets/italic-tool.png';
import UndoIcon from '../../assets/undo-tool.png';
import RedoIcon from '../../assets/redo-tool.png';
import { handleFlaggedWords, handleHighlight, isHighlighted } from './textEditor';

export const getEmergentTextEditorToolbar = (showColorPicker = false): TextEditorToolbar => {
  const toolbarOptions = {
    options: ['inline', 'history'],
    inline: {
      options: ['bold', 'italic'],
      bold: { icon: BoldIcon, className: 'text-editor-tool' },
      italic: { icon: ItalicIcon, className: 'text-editor-tool' },
    },
    colorPicker: {
      colors: TEXT_TOOL_COLORS,
      component: ColorPickerTool,
    },
    history: {
      inDropdown: false,
      options: ['undo', 'redo'],
      undo: { icon: UndoIcon, className: 'text-editor-tool' },
      redo: { icon: RedoIcon, className: 'text-editor-tool' },
    },
  };

  if (showColorPicker) {
    toolbarOptions.options.splice(1, 0, 'colorPicker');
  }

  return toolbarOptions;
};

const getEmergentEditorLineFromWords = (words: ExemplarWord[], isTitle = false): RawDraftContentBlock => {
  let editorBlock: RawDraftContentBlock = {
    type: isTitle ? BLOCK_TYPES.headerTwo : BLOCK_TYPES.unStyled,
    key: uuid(),
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {},
    text: '',
  };

  // eslint-disable-next-line complexity
  words.map(word => {
    const isBold = word.style?.includes('bold');
    const isItalic = word.style?.includes('italic');
    const isNospacing = word.style?.includes('nospacing');
    const isColored = word.style?.includes('highlighted');
    const isSpaceNoNeeded =
      isNospacing || !editorBlock.text.length || editorBlock.text[editorBlock.text.length - 1] === '\n';
    const styleOffset = isSpaceNoNeeded ? 0 : 1;

    if (LINE_BREAKS.includes(word.text)) {
      editorBlock = {
        ...editorBlock,
        text: `${editorBlock.text}\n`,
      };
      return;
    }

    isItalic &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: 'ITALIC',
      });

    isBold &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: 'BOLD',
      });

    isHighlighted(word) && handleHighlight(word, editorBlock, styleOffset);

    isColored &&
      editorBlock.inlineStyleRanges.push({
        offset: editorBlock.text.length + styleOffset,
        length: word.text.length,
        style: `color-${word.color || COLORS.usafaBlue}` as DraftInlineStyleType,
      });

    editorBlock = {
      ...editorBlock,
      text: `${editorBlock.text}${isSpaceNoNeeded ? '' : ' '}${word.text || ''}`,
    };
  });

  return editorBlock;
};

export const getEmergentEditorStateFromExemplarLines = (section: Section): RawDraftContentState => {
  const editorState: RawDraftContentState = {
    entityMap: {},
    blocks: [],
  };

  if ('lines' in section) {
    section.lines.map(line => {
      const isHeading = section.style?.includes('heading');
      editorState.blocks.push(getEmergentEditorLineFromWords(line.words, isHeading));
    });
  }

  return editorState;
};

export const getCurrentPageEmergentContent = (
  content: EmergentExemplar['content'],
  page: string
): WordsAndSentencesContent => {
  return content.reduce((pageToParse: WordsAndSentencesContent, currentPage) => {
    if (currentPage.label.toLowerCase().split(' ').join('-') === page) {
      pageToParse = currentPage as WordsAndSentencesContent;
    }
    return pageToParse;
  }, {} as WordsAndSentencesContent);
};

export const getEmergentEditorStateFromSide = (
  side: Array<EmergentContentSection | Image>
): EmergentTextEditorSideState => {
  return side.reduce((lines, section) => {
    if (section.type === 'section') {
      return {
        ...lines,
        [section.id as string]: {
          value: EditorState.createWithContent(
            convertFromRaw(getEmergentEditorStateFromExemplarLines(section as Section))
          ),
          meta: {
            ...(section.color && { color: section.color }),
          },
        },
      };
    }

    return lines;
  }, {});
};

export const getEmergentLineFromEditorBlock = (
  editorBlock: RawDraftContentBlock,
  sectionId: string,
  sectionIndex: number,
  lineIndex: number,
  contentSide: string,
  label: string
): ContentLine => {
  const line: ContentLine = {
    words: [],
  };

  if (!editorBlock.text.length) {
    return line;
  }

  let currentOffset = 0;
  const arrayOfWords: Array<string> = parseWordsAndSymbols(editorBlock.text);

  arrayOfWords
    .filter(word => word === '\n' || word.trim())
    .map((word: string, index: number) => {
      let color;
      const defaultStyles: string[] = [];
      const flaggedWords: string[] = [];
      const newWord: ExemplarWord = {
        id: uuid(),
        lineId: editorBlock.key as string,
        text: word === '\n' ? '\n\n' : word,
        wordId: uuid(),
        wordIndex: index,
        sectionId: sectionId,
        sectionIndex,
        lineIndex,
        side: contentSide,
        type: 'section',
        label,
      };

      const currentOffsetEnd = currentOffset + word.length - 1;

      editorBlock.inlineStyleRanges.map(style => {
        const styleOffset = style.offset;
        const styleOffsetEnd = style.offset + style.length - 1;

        if (
          (currentOffset >= styleOffset && currentOffset <= styleOffsetEnd) ||
          (currentOffset <= styleOffset && currentOffsetEnd >= styleOffset)
        ) {
          const isDefault = DEFAULT_STYLES.some(defaultStyle => {
            return style?.style.toLowerCase() === defaultStyle;
          });

          const isFontColor = TEXT_TOOL_COLORS.some(color => {
            return style?.style.toLowerCase() === `color-${color.toLowerCase()}`;
          });

          if (isDefault) {
            defaultStyles.push(style.style.toLowerCase());
            return;
          }

          if (isFontColor) {
            color = style.style?.toLowerCase().replace('color-', '');
            defaultStyles.push('highlighted');
          }

          flaggedWords.push(style.style);
        }

        return;
      });

      if (color) {
        newWord.color = color;
      }

      handleFlaggedWords(flaggedWords, newWord, word);

      if (index > 0 && ![' ', '\n'].includes(editorBlock.text[currentOffset - 1])) {
        defaultStyles.push('nospacing');
      }

      if (defaultStyles.length) {
        newWord.style = defaultStyles;
      }

      currentOffset += word.length + (editorBlock.text[currentOffsetEnd + 1] === ' ' ? 1 : 0);

      line.words.push(newWord);
    });

  return line;
};

export const getEmergentSectionFromEditorSide = (
  editorSection: EmergentTextEditorSection,
  sectionIndex: number,
  contentSide: string,
  label: string
): Section => {
  const isTitle = contentSide === 'left' && sectionIndex === 0;
  const section: Section = {
    ...editorSection.meta,
    type: 'section',
    lines: [],
    id: uuid(),
  };

  if (isTitle) {
    section.style = ['heading'];
  }

  const rowEditorSection = convertToRaw(editorSection.value.getCurrentContent());
  rowEditorSection.blocks.map((block, lineIndex) => {
    section.lines.push(getEmergentLineFromEditorBlock(block, section.id, sectionIndex, lineIndex, contentSide, label));
  });

  return section;
};

export const getEmergentSideFromEditorState = (
  editorState: EmergentTextEditorSideState,
  contentSide: string,
  label: string
): Section[] => {
  const side: Section[] = [];

  Object.entries(editorState).map(([, editorSection], index) => {
    side.push(getEmergentSectionFromEditorSide(editorSection, index, contentSide, label));
  });

  return side;
};

export const getNewEmergentFromSides = (
  exemplar: EmergentExemplar,
  leftContent: EmergentTextEditorSideState,
  rightContent: EmergentTextEditorSideState,
  partOfExemplar: WordsAndSentencesContent
): EmergentExemplar => {
  const left = getEmergentSideFromEditorState(leftContent, 'left', partOfExemplar.label);
  const right = getEmergentSideFromEditorState(rightContent, 'right', partOfExemplar.label);

  const leftImages = partOfExemplar.content.left.filter(section => {
    return section.type !== 'section';
  });
  const rightImages = partOfExemplar.content.right.filter(section => {
    return section.type !== 'section';
  });

  const filteredContent = exemplar.content.filter(page => {
    return page.label !== partOfExemplar.label;
  });

  const newPartial = {
    ...partOfExemplar,
    content: {
      left: [...left, ...leftImages],
      right: [...right, ...rightImages],
    },
  };

  const title = convertSectionToString(left[0]);

  return {
    ...exemplar,
    title: title,
    content: [...filteredContent, newPartial],
  };
};
